import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import CaseWorkspaceHeader from './CaseWorkspaceHeader';
import CaseWorkspaceSidebar from './CaseWorkspaceSidebar';
import EvidenceDrawer from '../../shared/EvidenceDrawer';
import SimulationPromptBanner from '../../shared/SimulationPromptBanner';
import OmbudsmanReport from './MiddlePanel/OmbudsmanReport';
import ChatPanel from './RightPanel/ChatPanel';
import SimulationHistoryBar from './SimulationHistoryBar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { Button } from '../../../ui/button';
import { DocumentIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { Scale } from 'lucide-react';
import { CaseWorkspaceState } from '../../../../types/case-workspace';
import { useSimulationStream } from '../../../../hooks/useSimulationStream';
import { useDecisionModal } from '../../../../hooks/useDecisionModal';
import { Simulation } from '../../../../types/ombudsman';
import { fetchWithAuth, getEvidenceForSimulation, toggleEvidenceInSimulation, getSignalById, getEvidenceForSignal, createSimulation } from '../../../../utils/api';
import FOSDecisionDetailModal from '../../../ombudsman/FOSDecisionDetailModal';
import { useNavigation } from '../../../../contexts/NavigationContext';
import { useWebSocket } from '../../../../contexts/WebSocketContext';
import { useCaseWorkspaceChat } from './useCaseWorkspaceChat';

const CaseWorkspace: React.FC = () => {
  const { simulationId } = useParams<{ simulationId: string }>();
  const navigate = useNavigate();
  const { getBackPath, navigateWithReferrer } = useNavigation();

  // Decision modal
  const decisionModal = useDecisionModal();

  // Case data
  const [caseTitle, setCaseTitle] = useState('Complaint Analysis: Payment Protection Claim');
  const isEditingTitle = false;

  // Evidence - using new hook-based approach
  const [showSimulationPrompt, setShowSimulationPrompt] = useState(false);
  const [newEvidenceCount, setNewEvidenceCount] = useState(0);
  const promptedEvidenceIdsRef = useRef<Set<string>>(new Set());

  // Report sections
  const [reportSections, setReportSections] = useState<CaseWorkspaceState['reportSections']>({
    outcome: null,
    complaintSummary: null,
    whatHappened: null,
    reasoning: null,
    redressGuidance: null,
    precedents: null,
  });
  const [streamingSection, setStreamingSection] = useState<CaseWorkspaceState['streamingSection']>(null);

  // Precedents
  const [expandedPrecedents, setExpandedPrecedents] = useState<string[]>([]);

  // Store simulation data (including ML prediction)
  const [simulation, setSimulation] = useState<Simulation | null>(null);
  
  // Store signal data for verbatim text and status
  const [signal, setSignal] = useState<any>(null);
  const [signalProgressStage, setSignalProgressStage] = useState<string | null>(null);
  const [signalProgressPercent, setSignalProgressPercent] = useState<number | null>(null);
  
  
  // WebSocket for signal updates
  const { subscribe, unsubscribe, addMessageListener } = useWebSocket();

  // Use simulation streaming hook
  const {
    sections: streamingSections,
    messages: streamingMessages,
    isStreaming,
    error: streamError,
    streamingSection: currentStreamingSection,
    currentStatus,
    progressMessage,
  } = useSimulationStream(simulationId || null);

  // Get suggested questions from streaming data
  const suggestedQuestions = streamingSections.suggestedQuestions || [];
  const {
    messages,
    inputMessage,
    isTyping,
    setInputMessage,
    sendMessage: handleSendMessage,
  } = useCaseWorkspaceChat({
    simulationId: simulationId || null,
    streamingMessages,
  });

  // Fetch simulation data to get ML prediction
  useEffect(() => {
    if (!simulationId) return;

    const fetchSimulation = async () => {
      try {
        const data = await fetchWithAuth(
          `/v1/ombudsman-decisions/simulations/${simulationId}`
        ) as Simulation;
        setSimulation(data);
      } catch (error) {
        console.error('Failed to fetch simulation:', error);
      }
    };

    fetchSimulation();
  }, [simulationId]);

  // Fetch signal data when simulation loads
  useEffect(() => {
    if (!simulation?.signal_id) return;

    const fetchSignal = async () => {
      try {
        const signalData = await getSignalById(simulation.signal_id);
        setSignal(signalData);
      } catch (error) {
        console.error('Failed to fetch signal:', error);
      }
    };

    fetchSignal();
  }, [simulation?.signal_id]);

  // Subscribe to signal WebSocket updates
  useEffect(() => {
    if (!simulation?.signal_id) return;

    subscribe('signal', simulation.signal_id);

    const handleMessage = (data: any) => {
      const updateSignalId = data.signal_id || data.data?.signal_id || data.data?.id;
      if (updateSignalId !== simulation.signal_id) return;

      console.log('Received real-time signal update:', data);
      
      if (data.type === 'signal.updated' || data.type === 'signal.classified' || data.type === 'signal.progress') {
          // For signal.updated and signal.classified, refetch the complete signal to get all fields
          if (data.type === 'signal.updated' || data.type === 'signal.classified') {
            getSignalById(simulation.signal_id).then(updatedSignal => {
              setSignal(updatedSignal);
              
              // Clear progress when classified/updated
              if (data.type === 'signal.classified') {
                setSignalProgressStage(null);
                setSignalProgressPercent(null);
              }
            
              // Update title if signal now has a summary (short title) and title hasn't been manually edited
              if (updatedSignal.summary) {
                setCaseTitle(prevTitle => {
                  // Only preserve if manually edited, otherwise always update to signal summary
                  if (!isEditingTitle) {
                    return updatedSignal.summary;
                  }
                  return prevTitle;
                });
              }
            }).catch(err => {
              console.error('Failed to fetch updated signal:', err);
            });
        } else if (data.type === 'signal.progress') {
          // For progress updates, update processing_status from the update
          const processingStatus = data.data?.processing_status || data.data?.status || data.processing_status;
          const stage = data.data?.stage || data.stage;
          const percent = data.data?.percent || data.percent;
          
          // Update progress stage and percent for real-time display
          if (stage) {
            setSignalProgressStage(stage);
          }
          if (typeof percent === 'number') {
            setSignalProgressPercent(percent);
          }
          
          if (processingStatus) {
            setSignal((prev: any) => prev ? { 
              ...prev, 
              processing_status: processingStatus 
            } : null);
          }
        }
      }
    };

    const cleanup = addMessageListener(handleMessage);

    return () => {
      unsubscribe('signal', simulation.signal_id);
      cleanup();
    };
  }, [simulation?.signal_id, subscribe, unsubscribe, addMessageListener, isEditingTitle]);

  // Update case title from simulation or signal data
  useEffect(() => {
    const defaultTitle = 'Complaint Analysis: Payment Protection Claim';
    
    // Prefer signal summary (short title) if available
    if (signal?.summary) {
      setCaseTitle(prevTitle => {
        // Always prefer signal summary unless manually edited
        if (!isEditingTitle && prevTitle !== signal.summary) {
          return signal.summary;
        }
        return prevTitle;
      });
    } else if (simulation) {
      // Fallback to simulation title (only if signal summary not available and not manually edited)
      setCaseTitle(prevTitle => {
        if (!isEditingTitle && (prevTitle === defaultTitle || !prevTitle)) {
          const simulationTitle = (simulation as any)?.signal_summary || (simulation as any)?.name;
          return simulationTitle || prevTitle;
        }
        return prevTitle;
      });
    }
  }, [simulation, signal?.summary, isEditingTitle]); // Include isEditingTitle in dependencies

  // Handle evidence upload during simulation
  const handleEvidenceUploaded = useCallback((evidenceId: string) => {
    // Don't show prompt immediately - wait for processing to complete
  }, []);

  // Handle evidence completed - only show prompt if structured_text is available
  const handleEvidenceCompleted = useCallback(async (evidenceId: string) => {
    if (!simulationId || !simulation?.signal_id) return;
    
    // Skip if we've already prompted for this evidence
    if (promptedEvidenceIdsRef.current.has(evidenceId)) return;
    
    // Add a small delay to ensure backend has persisted structured_text
    // (the WebSocket message might arrive slightly before DB commit)
    const checkEvidence = async (retries = 2): Promise<void> => {
      try {
        // Fetch evidence list to check if this evidence has structured_text
        const evidenceList = await getEvidenceForSignal(simulation.signal_id) as any[];
        const completedEvidence = evidenceList.find((e: any) => e.id === evidenceId);
        
        // Only show prompt if evidence has structured_text (processed and ready for LLM)
        if (completedEvidence?.structured_text) {
          promptedEvidenceIdsRef.current.add(evidenceId);
          setNewEvidenceCount(prev => prev + 1);
          setShowSimulationPrompt(true);
        } else if (retries > 0) {
          // Retry after a short delay in case structured_text hasn't been persisted yet
          setTimeout(() => checkEvidence(retries - 1), 500);
        }
      } catch (error) {
        console.error('Failed to fetch evidence details:', error);
        if (retries > 0) {
          setTimeout(() => checkEvidence(retries - 1), 500);
        }
      }
    };
    
    // Small initial delay to allow backend to commit
    setTimeout(() => checkEvidence(), 300);
  }, [simulationId, simulation?.signal_id]);

  const handleRunSimulation = useCallback(async () => {
    if (!simulation?.signal_id || !simulationId) return;
    
    try {
      // Fetch evidence for the current simulation to get used_in_reasoning flags
      const evidenceList = await getEvidenceForSimulation(simulationId) as any[];
      
      // Include ALL completed evidence (both previously used and new)
      const allCompletedEvidence = evidenceList.filter(
        (ev: any) => ev.structured_text && 
        ev.parsing_status === 'completed'
      );
      
      if (allCompletedEvidence.length === 0) {
        toast.error('No evidence available to include in simulation');
        return;
      }
      
      // Create simulation with all completed evidence (both used and unused)
      const evidenceIds = allCompletedEvidence.map((e: any) => e.id);
      const newSimulation = await createSimulation(simulation.signal_id, {
        evidence_ids: evidenceIds
      });
      
      if (!newSimulation || !newSimulation.id) {
        console.error('Invalid simulation response:', newSimulation);
        toast.error('Failed to create case: Invalid response from server');
        return;
      }

      toast.success('New case started');

      // Navigate to the new case
      navigate(`/v3/ombudsman/simulations/${newSimulation.id}`);
    } catch (error: any) {
      console.error('Failed to create case:', error);
      const errorMessage = error?.message || error?.error?.message || 'Unknown error';
      toast.error(`Failed to create case: ${errorMessage}`);
    }
  }, [simulation, simulationId, navigate]);

  const handleDismissPrompt = useCallback(() => {
    setShowSimulationPrompt(false);
    setNewEvidenceCount(0);
    // Don't clear promptedEvidenceIds - we want to avoid re-prompting for the same evidence
    // Note: The persistent "Run New Simulation" button in EvidenceDrawer will remain visible
  }, []);

  // Handle new evidence detection from EvidenceDrawer
  const handleNewEvidenceDetected = useCallback((_hasNew: boolean) => {}, []);

  // Refetch simulation when streaming completes to get final ML prediction
  useEffect(() => {
    if (!isStreaming && simulationId) {
      const fetchSimulation = async () => {
        try {
          const data = await fetchWithAuth(
            `/v1/ombudsman-decisions/simulations/${simulationId}`
          ) as Simulation;
          setSimulation(data);
        } catch (error) {
          console.error('Failed to refetch simulation:', error);
        }
      };
      // Small delay to ensure backend has saved ML prediction
      const timeoutId = setTimeout(fetchSimulation, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [isStreaming, simulationId]);

  // Detect mobile
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Redirect if no simulation ID
  useEffect(() => {
    if (!simulationId) {
      navigate('/v3/workspace/ombudsman');
    }
  }, [simulationId, navigate]);

  // Update report sections from streaming data and ML prediction
  useEffect(() => {
    setReportSections(prev => {
      let next = { ...prev };
      let changed = false;
      const mlPrediction = simulation?.simulation_metadata?.ml_prediction;

      if (streamingSections.outcome) {
        // Prefer ML prediction values if available
        const outcome = mlPrediction?.decision_status || streamingSections.outcome.outcome;
        const confidence = mlPrediction 
          ? Math.round(mlPrediction.probability_upheld * 100)
          : streamingSections.outcome.confidence;

        next = {
          ...next,
          outcome: {
            outcome: outcome as 'upheld' | 'not_upheld' | 'partially_upheld',
            confidence,
            rationale: streamingSections.outcome.rationale || prev.outcome?.rationale,
          },
        };
        changed = true;
      } else if (mlPrediction && prev.outcome) {
        // Apply ML values even if outcome section already exists
        const mlOutcome = mlPrediction.decision_status;
        const mlConfidence = Math.round(mlPrediction.probability_upheld * 100);
        if (prev.outcome.outcome !== mlOutcome || prev.outcome.confidence !== mlConfidence) {
          next = {
            ...next,
            outcome: {
              ...prev.outcome,
              outcome: mlOutcome as 'upheld' | 'not_upheld' | 'partially_upheld',
              confidence: mlConfidence,
            },
          };
          changed = true;
        }
      }

      if (streamingSections.summary !== null) {
        next = {
          ...next,
          complaintSummary: { summary: streamingSections.summary },
        };
        changed = true;
      }

      if (streamingSections.timeline !== null) {
        next = {
          ...next,
          whatHappened: { events: streamingSections.timeline },
        };
        changed = true;
      }

      if (streamingSections.reasoning) {
        // Only use references from backend - don't fallback to keyFactors
        // If references contain DRNs, use them; otherwise keep existing or empty array
        const references = streamingSections.reasoning.references?.length
          ? streamingSections.reasoning.references
          : prev.reasoning?.references || [];

        next = {
          ...next,
          reasoning: {
            narrative: streamingSections.reasoning.narrative,
            references,
          },
        };
        changed = true;
      }

      if (streamingSections.redressGuidance) {
        // Prefer ML prediction interval if available
        const mlMonetaryRange = mlPrediction?.prediction_interval
          ? {
              min: mlPrediction.prediction_interval.lower_gbp,
              max: mlPrediction.prediction_interval.upper_gbp,
            }
          : null;

        next = {
          ...next,
          redressGuidance: {
            monetaryRange:
              mlMonetaryRange ||
              streamingSections.redressGuidance.monetaryRange ||
              prev.redressGuidance?.monetaryRange ||
              { min: 0, max: 0 },
            types:
              streamingSections.redressGuidance.types ||
              prev.redressGuidance?.types ||
              [],
            remedies:
              streamingSections.redressGuidance.remedies ||
              prev.redressGuidance?.remedies ||
              [],
            notes:
              streamingSections.redressGuidance.notes ||
              prev.redressGuidance?.notes ||
              '',
            precedents:
              streamingSections.redressGuidance.precedents ||
              prev.redressGuidance?.precedents ||
              [],
            compensationBreakdown:
              streamingSections.compensation ||
              streamingSections.redressGuidance.compensationBreakdown ||
              prev.redressGuidance?.compensationBreakdown,
          },
        };
        changed = true;
      } else if (mlPrediction?.prediction_interval && prev.redressGuidance) {
        // Apply ML interval even if redress guidance already exists
        const mlMonetaryRange = {
          min: mlPrediction.prediction_interval.lower_gbp,
          max: mlPrediction.prediction_interval.upper_gbp,
        };
        const currentRange = prev.redressGuidance.monetaryRange;
        if (currentRange.min !== mlMonetaryRange.min || currentRange.max !== mlMonetaryRange.max) {
          next = {
            ...next,
            redressGuidance: {
              ...prev.redressGuidance,
              monetaryRange: mlMonetaryRange,
            },
          };
          changed = true;
        }
      } else if (streamingSections.compensation && next.redressGuidance) {
        next = {
          ...next,
          redressGuidance: {
            ...next.redressGuidance,
            compensationBreakdown: streamingSections.compensation,
          },
        };
        changed = true;
      }

      if (streamingSections.precedents !== null) {
        next = {
          ...next,
          precedents: streamingSections.precedents,
        };
        
        // Update reasoning references with DRNs from precedents if references are missing or contain descriptive text
        if (next.precedents && next.precedents.length > 0) {
          const drnReferences = next.precedents
            .map(p => p.drn)
            .filter(drn => drn && drn.startsWith('DRN-'));
          
          if (drnReferences.length > 0) {
            const currentReferences = next.reasoning?.references ?? [];
            const hasValidDRNs = currentReferences.some(ref => ref && ref.startsWith('DRN-'));
            
            // Only update if current references don't have valid DRNs
            if (!hasValidDRNs) {
              next = {
                ...next,
                reasoning: next.reasoning
                  ? {
                      ...next.reasoning,
                      references: drnReferences,
                    }
                  : null,
              };
            }
          }
        }
        
        changed = true;
      }

      if (changed) {
        return next;
      }

      return prev;
    });
  }, [streamingSections, simulation]);

  // Update streaming section indicator
  useEffect(() => {
    setStreamingSection(currentStreamingSection);
  }, [currentStreamingSection]);

  useEffect(() => {
    if (isStreaming) {
      setReportSections({
        outcome: null,
        complaintSummary: null,
        whatHappened: null,
        reasoning: null,
        redressGuidance: null,
        precedents: null,
      });
      setExpandedPrecedents([]);
    }
  }, [isStreaming]);

  const handleTogglePrecedent = (drn: string) => {
    setExpandedPrecedents(prev =>
      prev.includes(drn) ? prev.filter(d => d !== drn) : [...prev, drn]
    );
  };

  const handleExpandAllPrecedents = () => {
    if (reportSections.precedents) {
      setExpandedPrecedents(reportSections.precedents.map(p => p.drn));
    }
  };

  const handleToggleEvidenceInReasoning = async (evidenceId: string, used: boolean) => {
    if (!simulationId) return;
    
    try {
      await toggleEvidenceInSimulation(simulationId, evidenceId, used);
    } catch (error) {
      console.error('Failed to toggle evidence:', error);
      toast.error('Failed to update evidence');
    }
  };

  const handleExportPDF = () => {
    // TODO: API call to export PDF
    console.log('Export PDF');
  };

  const handleDownloadJSON = () => {
    // TODO: API call to download JSON
    console.log('Download JSON');
  };

  const handleProvideFeedback = () => {
    // TODO: Open feedback dialog
    console.log('Provide feedback');
  };

  const handleArchive = () => {
    // TODO: API call to archive
    console.log('Archive case');
  };

  const [isRerunning, setIsRerunning] = useState(false);
  const handleRerunSimulation = async () => {
    const signalId = simulation?.signal_id;
    if (!signalId) {
      toast.error('Cannot rerun: no signal associated with this simulation');
      return;
    }
    setIsRerunning(true);
    try {
      const newSimulation = await createSimulation(signalId);
      if (newSimulation?.id) {
        toast.success('New simulation started', {
          description: 'Analyzing against historical ombudsman decisions...',
        });
        navigate(`/v3/ombudsman/simulations/${newSimulation.id}`);
      }
    } catch (err: any) {
      console.error('Failed to rerun simulation:', err);
      toast.error(err.message || 'Failed to start new simulation');
    } finally {
      setIsRerunning(false);
    }
  };

  const caseIdLabel = simulationId ? `CG-${simulationId.replace(/-/g, '').slice(0, 8).toUpperCase()}` : 'Case';
  const signalId = simulation?.signal_id;
  const productLabel = signal?.product_name || signal?.product_type || signal?.channel || 'Complaint';

  const handleBack = () => {
    const backPath = getBackPath();
    navigate(backPath);
  };

  const handleViewSignal = () => {
    if (signalId) {
      // Navigate to V4 records view for the signal
      navigateWithReferrer(`/v4/records/default/${signalId}`, `/v3/ombudsman/simulations/${simulationId}`);
    }
  };

  // Error state
  if (streamError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-6">
        <h3 className="text-lg font-semibold mb-2">Case Guidance Failed</h3>
        <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
          {streamError}
        </p>
        <Button onClick={() => navigate('/v3/ombudsman')}>
          Back to Workspace
        </Button>
      </div>
    );
  }

  // Mobile view with tabs
  if (isMobile) {
    return (
      <div className="flex h-full flex-col bg-[var(--go-spring)]">
        <CaseWorkspaceHeader
          caseIdLabel={caseIdLabel}
          caseTitle={caseTitle}
          openedAt={simulation?.created_at}
          lastActiveAt={simulation?.updated_at}
          productLabel={productLabel}
          signalStatus={signal?.status}
          signalProcessingStatus={signal?.processing_status}
          signalProgressStage={signalProgressStage}
          signalProgressPercent={signalProgressPercent}
          onBack={handleBack}
          onViewSignal={signalId ? handleViewSignal : undefined}
          onExportPDF={handleExportPDF}
          onDownloadJSON={handleDownloadJSON}
          onProvideFeedback={handleProvideFeedback}
          onRerunSimulation={handleRerunSimulation}
          isRerunning={isRerunning}
          onArchive={handleArchive}
        />

        {simulation?.signal_id && simulationId && (
          <SimulationHistoryBar
            signalId={simulation.signal_id}
            currentSimulationId={simulationId}
            onNavigate={(simId) => navigateWithReferrer(`/v3/ombudsman/simulations/${simId}`, window.location.pathname)}
          />
        )}

        <Tabs defaultValue="report" className="flex-1 flex flex-col">
          <TabsList className="w-full">
            <TabsTrigger value="evidence" className="flex-1">
              <DocumentIcon className="w-4 h-4 mr-2" />
              Evidence
            </TabsTrigger>
            <TabsTrigger value="report" className="flex-1">
              <Scale className="w-4 h-4 mr-2" />
              Report
            </TabsTrigger>
            <TabsTrigger value="chat" className="flex-1">
              <ChatBubbleLeftIcon className="w-4 h-4 mr-2" />
              Chat
            </TabsTrigger>
          </TabsList>

          <TabsContent value="evidence" className="flex-1 overflow-hidden">
            {simulation?.signal_id && (
              <>
                {showSimulationPrompt && (
                  <div className="p-4">
                    <SimulationPromptBanner
                      onRunSimulation={handleRunSimulation}
                      onDismiss={handleDismissPrompt}
                      evidenceCount={newEvidenceCount}
                    />
                  </div>
                )}
                <div className="p-4 h-full overflow-auto">
                  <EvidenceDrawer
                    signalId={simulation.signal_id}
                    orientation="vertical"
                    simulationId={simulationId}
                    onEvidenceUploaded={handleEvidenceUploaded}
                    onEvidenceCompleted={handleEvidenceCompleted}
                    onToggleEvidenceInReasoning={handleToggleEvidenceInReasoning}
                    onNewEvidenceDetected={handleNewEvidenceDetected}
                  />
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="report" className="flex-1 overflow-auto">
            <OmbudsmanReport
              reportSections={reportSections}
              streamingSection={streamingSection}
              isStreaming={isStreaming}
              expandedPrecedents={expandedPrecedents}
              onTogglePrecedent={handleTogglePrecedent}
              onExpandAllPrecedents={handleExpandAllPrecedents}
              onDecisionClick={decisionModal.openDecision}
              signalVerbatim={signal?.verbatim}
            />
          </TabsContent>

          <TabsContent value="chat" className="flex-1 overflow-hidden">
            <ChatPanel
              messages={messages}
              inputMessage={inputMessage}
              isTyping={isTyping}
              suggestedQuestions={suggestedQuestions}
              currentStatus={currentStatus}
              progressMessage={progressMessage}
              activatedSkills={streamingSections.activatedSkills}
              toolCalls={streamingSections.toolCalls}
              agentProgress={streamingSections.agentProgress}
              isSimulationComplete={!isStreaming}
              onInputChange={setInputMessage}
              onSendMessage={handleSendMessage}
              isOpen={true}
              onClose={() => {}}
              onDecisionClick={decisionModal.openDecision}
              showHeader={false}
            />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // Desktop view with three panels
  return (
    <div className="h-full overflow-auto bg-[var(--go-spring)]">
      <div className="w-full px-4 py-4 md:px-6 md:py-5 xl:px-8 xl:py-6">
        <CaseWorkspaceHeader
          caseIdLabel={caseIdLabel}
          caseTitle={caseTitle}
          openedAt={simulation?.created_at}
          lastActiveAt={simulation?.updated_at}
          productLabel={productLabel}
          signalStatus={signal?.status}
          signalProcessingStatus={signal?.processing_status}
          signalProgressStage={signalProgressStage}
          signalProgressPercent={signalProgressPercent}
          onBack={handleBack}
          onViewSignal={signalId ? handleViewSignal : undefined}
          onExportPDF={handleExportPDF}
          onDownloadJSON={handleDownloadJSON}
          onProvideFeedback={handleProvideFeedback}
          onRerunSimulation={handleRerunSimulation}
          isRerunning={isRerunning}
          onArchive={handleArchive}
        />

        {simulation?.signal_id && simulationId && (
          <div className="mb-5">
            <SimulationHistoryBar
              signalId={simulation.signal_id}
              currentSimulationId={simulationId}
              onNavigate={(simId) => navigateWithReferrer(`/v3/ombudsman/simulations/${simId}`, window.location.pathname)}
            />
          </div>
        )}

        <div className="flex items-start gap-5">
          <div className="min-w-0 flex-1 overflow-hidden rounded-lg border border-border bg-background">
            <OmbudsmanReport
              reportSections={reportSections}
              streamingSection={streamingSection}
              isStreaming={isStreaming}
              expandedPrecedents={expandedPrecedents}
              onTogglePrecedent={handleTogglePrecedent}
              onExpandAllPrecedents={handleExpandAllPrecedents}
              onDecisionClick={decisionModal.openDecision}
              signalVerbatim={signal?.verbatim}
            />
          </div>

          <CaseWorkspaceSidebar
            signalId={simulation?.signal_id}
            simulationId={simulationId}
            showSimulationPrompt={showSimulationPrompt}
            newEvidenceCount={newEvidenceCount}
            currentStatus={currentStatus}
            progressMessage={progressMessage}
            activatedSkills={streamingSections.activatedSkills}
            toolCalls={streamingSections.toolCalls}
            agentProgress={streamingSections.agentProgress}
            isSimulationComplete={!isStreaming}
            messages={messages}
            inputMessage={inputMessage}
            isTyping={isTyping}
            suggestedQuestions={suggestedQuestions}
            onRunSimulation={handleRunSimulation}
            onDismissPrompt={handleDismissPrompt}
            onEvidenceUploaded={handleEvidenceUploaded}
            onEvidenceCompleted={handleEvidenceCompleted}
            onToggleEvidenceInReasoning={handleToggleEvidenceInReasoning}
            onNewEvidenceDetected={handleNewEvidenceDetected}
            onInputChange={setInputMessage}
            onSendMessage={handleSendMessage}
            onDecisionClick={decisionModal.openDecision}
          />
        </div>
      </div>

      {/* Decision Detail Modal */}
      <FOSDecisionDetailModal
        open={decisionModal.isOpen}
        onClose={decisionModal.closeDecision}
        decisionId={decisionModal.decisionId}
        loading={decisionModal.isLoading}
        decision={decisionModal.decision}
        error={decisionModal.error}
      />
    </div>
  );
};

export default CaseWorkspace;
